import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Sparkles, Play, Square } from "lucide-react";
import { Modal, Input, Textarea, Select, Checkbox, Button, StatusBadge, LoadingBlock, toast } from "@/components/ds";
import { api, formatApiError, type Estimate, type EstimateConfig } from "@/lib/api";
import { EST_STATUS, STATUS_OPTIONS, STEP_META, amountText, fmtDate } from "./constants";

export function EstimateManager({ estimateId, leadId, onClose, onSaved }: {
  estimateId?: string; leadId?: string; onClose: () => void; onSaved: () => void;
}) {
  const [config, setConfig] = useState<EstimateConfig | null>(null);
  const [est, setEst] = useState<Estimate | null>(null);
  const [busy, setBusy] = useState(false);
  const [aiFollow, setAiFollow] = useState(false);
  const [form, setForm] = useState({
    amount: "", amount_disclosed: true, service: "", description: "", valid_until: "", status: "estimate_sent",
  });
  const isEdit = !!estimateId;

  const loadEst = useCallback(async () => {
    if (!estimateId) return;
    const rows = await api.estimatesList({});
    const found = rows.find((r) => r.id === estimateId) ?? null;
    setEst(found);
    if (found) setForm({
      amount: found.amount != null ? String(found.amount) : "",
      amount_disclosed: found.amount_disclosed, service: found.service ?? "",
      description: found.description ?? "", valid_until: (found.valid_until ?? "").slice(0, 10),
      status: found.status,
    });
  }, [estimateId]);

  useEffect(() => { api.estimatesConfig().then(setConfig).catch(() => {}); }, []);
  useEffect(() => { loadEst(); }, [loadEst]);

  const body = () => ({
    amount: form.amount_disclosed && form.amount ? Number(form.amount) : null,
    amount_disclosed: form.amount_disclosed,
    service: form.service || null, description: form.description || null,
    valid_until: form.valid_until || null,
  });

  const save = async () => {
    setBusy(true);
    try {
      if (isEdit && est) { await api.estimateUpdate(est.id, body()); toast.success("Estimate updated"); }
      else { await api.estimateCreate({ ...body(), lead_id: leadId, status: form.status }); toast.success("Estimate created"); }
      onSaved(); if (isEdit) loadEst(); else onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const changeStatus = async (status: string) => {
    if (!est) return;
    setBusy(true);
    try { await api.estimateStatus(est.id, status); toast.success("Status updated"); await loadEst(); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const startFollowup = async () => {
    if (!est) return;
    setBusy(true);
    try { await api.estimateFollowupStart(est.id, aiFollow); toast.success("Follow-up started"); await loadEst(); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const stopFollowup = async () => {
    if (!est) return;
    setBusy(true);
    try { await api.estimateFollowupStop(est.id); toast.success("Follow-up stopped"); await loadEst(); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));
  const fu = est?.followup;
  const fuActive = fu?.status === "active";
  const closed = est ? ["accepted", "declined", "expired", "cancelled"].includes(est.status) : false;

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={isEdit ? "Manage estimate" : "Add / track estimate"}
      description="Estimate details reference only what you enter — ResFil never invents a price." testid="estimate-manager">
      <div className="max-h-[74vh] space-y-4 overflow-y-auto pr-1">
        {isEdit && !est ? <LoadingBlock rows={4} /> : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="mb-1 text-xs font-medium text-slate-500">Estimate amount</p>
                <Input type="number" min="0" value={form.amount} disabled={!form.amount_disclosed}
                  onChange={(e) => set("amount", e.target.value)} placeholder="e.g. 450" data-testid="estimate-amount" />
                <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-600">
                  <Checkbox checked={!form.amount_disclosed} onChange={(v: boolean) => set("amount_disclosed", !v)} testid="estimate-not-disclosed" />
                  Amount not disclosed
                </label>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium text-slate-500">Valid until (optional)</p>
                <Input type="date" value={form.valid_until} onChange={(e) => set("valid_until", e.target.value)} data-testid="estimate-valid-until" />
                <p className="mt-1 text-[11px] text-slate-400">Past this date the estimate auto-expires and follow-up stops.</p>
              </div>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">Service</p>
              <Input value={form.service} onChange={(e) => set("service", e.target.value)} placeholder="e.g. AC repair" data-testid="estimate-service" />
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">Notes (optional)</p>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="estimate-description" />
            </div>
            {!isEdit && (
              <div>
                <p className="mb-1 text-xs font-medium text-slate-500">Status</p>
                <Select value={form.status} onChange={(v) => set("status", v)} options={STATUS_OPTIONS} testid="estimate-status-new" />
              </div>
            )}

            {isEdit && est && (
              <>
                <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">Status</span>
                    <StatusBadge tone={(EST_STATUS[est.status] ?? EST_STATUS.estimate_requested).tone}>{(EST_STATUS[est.status] ?? EST_STATUS.estimate_requested).label}</StatusBadge>
                  </div>
                  <Select value={est.status} onChange={changeStatus} options={STATUS_OPTIONS} testid="estimate-status-select" />
                </div>

                <div className="rounded-lg border border-slate-200 p-3" data-testid="estimate-followup-panel">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-800">Automated follow-up</span>
                    {fu?.status && <StatusBadge tone={fuActive ? "blue" : "slate"}>{fu.status.replace(/_/g, " ")}</StatusBadge>}
                  </div>
                  {!config?.followup_included ? (
                    <p className="text-xs text-blue-600" data-testid="estimate-upgrade-note">Automated estimate follow-up is available on Growth and above. Upgrade to enable it.</p>
                  ) : !config?.sms_configured ? (
                    <p className="mb-2 flex items-start gap-1.5 text-xs text-amber-700"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> SMS is paused until Twilio is live — scheduled steps are marked “Unavailable — Configuration Required” and never sent.</p>
                  ) : null}
                  {fu?.steps && fu.steps.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {fu.steps.map((s, i) => {
                        const meta = STEP_META[s.state] ?? STEP_META.pending;
                        return (
                          <div key={i} className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs" data-testid={`estimate-step-${i}`}>
                            <span className="text-slate-500">{fmtDate(s.run_at)}</span>
                            <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
                            {s.ai_generated && <StatusBadge tone="blue"><Sparkles className="mr-0.5 inline h-3 w-3" />AI</StatusBadge>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {config?.followup_included && !closed && (
                    <div className="flex items-center gap-2">
                      {fuActive ? (
                        <Button size="sm" variant="outline" onClick={stopFollowup} disabled={busy} data-testid="estimate-followup-stop"><Square className="h-3.5 w-3.5" /> Stop follow-up</Button>
                      ) : (
                        <>
                          {config.ai_available && (
                            <label className="flex items-center gap-1.5 text-xs text-slate-600">
                              <Checkbox checked={aiFollow} onChange={(v: boolean) => setAiFollow(v)} testid="estimate-ai-toggle" /> AI-drafted messages
                            </label>
                          )}
                          <Button size="sm" onClick={startFollowup} disabled={busy} data-testid="estimate-followup-start"><Play className="h-3.5 w-3.5" /> Start follow-up</Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
      <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-4">
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button onClick={save} disabled={busy} data-testid="estimate-save-btn">{isEdit ? "Save changes" : "Create estimate"}</Button>
      </div>
    </Modal>
  );
}
